import api from './api';
import {
  Customer,
  CustomerStats,
  CreateCustomerDto,
  UpdateCustomerDto,
  CustomerShipmentHistory,
  CustomerAddress,
  CreateAddressDto
} from '../types/customer';

class CustomerService {
  private baseUrl = '/customers';

  // Get all customers with stats
  async getCustomers(): Promise<Customer[]> {
    try {
      const response = await api.get<Customer[]>(this.baseUrl);
      // Ensure we always return an array, even if API returns null or undefined
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching customers:', error);
      throw error;
    }
  }

  // Get customer statistics
  async getCustomerStats(): Promise<CustomerStats> {
    try {
      const response = await api.get<CustomerStats>(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer stats:', error);
      throw error;
    }
  }

  // Get customer by ID
  async getCustomer(id: number): Promise<Customer> {
    try {
      const response = await api.get<Customer>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer:', error);
      throw error;
    }
  }

  // Create new customer
  async createCustomer(customerData: CreateCustomerDto): Promise<Customer> {
    try {
      const response = await api.post<Customer>(this.baseUrl, customerData);
      return response.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  }

  // Update customer
  async updateCustomer(id: number, customerData: UpdateCustomerDto): Promise<Customer> {
    try {
      const response = await api.put<Customer>(`${this.baseUrl}/${id}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  }

  // Delete customer
  async deleteCustomer(id: number): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  }

  // Get customer shipments
  async getCustomerShipments(customerId: number): Promise<CustomerShipmentHistory> {
    try {
      const response = await api.get<CustomerShipmentHistory>(`${this.baseUrl}/${customerId}/shipments`);
      return response.data;
    } catch (error) {
      console.error('Error fetching customer shipments:', error);
      throw error;
    }
  }

  // Add address to customer
  async addCustomerAddress(customerId: number, addressData: CreateAddressDto): Promise<CustomerAddress> {
    try {
      const response = await api.post<CustomerAddress>(`${this.baseUrl}/${customerId}/addresses`, addressData);
      return response.data;
    } catch (error) {
      console.error('Error adding customer address:', error);
      throw error;
    }
  }

  // Update customer address
  async updateCustomerAddress(customerId: number, addressId: number, addressData: CreateAddressDto): Promise<CustomerAddress> {
    try {
      const response = await api.put<CustomerAddress>(`${this.baseUrl}/${customerId}/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer address:', error);
      throw error;
    }
  }

  // Delete customer address
  async deleteCustomerAddress(customerId: number, addressId: number): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/${customerId}/addresses/${addressId}`);
    } catch (error) {
      console.error('Error deleting customer address:', error);
      throw error;
    }
  }

  // Helper method to format customer display name
  formatCustomerName(customer: Customer): string {
    return `${customer.company_name} (${customer.contact_person})`;
  }

  // Helper method to format customer status
  formatCustomerStatus(status: string): string {
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  // Helper method to format address
  formatAddress(address: CustomerAddress): string {
    const parts = [
      address.address_line,
      address.city,
      address.region,
      address.postal_code,
      address.country
    ].filter(Boolean);
    
    return parts.join(', ');
  }
}

const customerService = new CustomerService();
export default customerService;
